import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyAssujetti, getMyEstablishments, listDeclarations, getRenewalCalendar } from '../services/assujettiService';

interface KPI { label: string; value: string; note: string; noteColor?: string }

export default function V4DashboardPage() {
  const [assujetti, setAssujetti] = useState<any>(null);
  const [establishments, setEstablishments] = useState<any[]>([]);
  const [declarations, setDeclarations] = useState<any[]>([]);
  const [calendar, setCalendar] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, e, d, c] = await Promise.allSettled([
          getMyAssujetti(),
          getMyEstablishments(),
          listDeclarations(),
          getRenewalCalendar(),
        ]);
        if (a.status === 'fulfilled') setAssujetti(a.value);
        if (e.status === 'fulfilled') setEstablishments(Array.isArray(e.value) ? e.value : e.value?.establishments ?? []);
        if (d.status === 'fulfilled') setDeclarations(Array.isArray(d.value) ? d.value : d.value?.declarations ?? []);
        if (c.status === 'fulfilled') setCalendar(Array.isArray(c.value) ? c.value : c.value?.renewals ?? []);
      } catch (err: any) {
        setError(err.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-gray-500">Chargement...</div>;
  if (error) return <div className="p-8 text-center text-red-600">{error}</div>;

  const estCount = establishments.length;
  const validLicenses = establishments.filter((e: any) => e.license_status === 'valid').length;
  const expiringSoon = establishments.filter((e: any) => e.days_remaining != null && e.days_remaining <= 90 && e.days_remaining > 0).length;
  const expired = establishments.filter((e: any) => e.license_status === 'expired' || (e.days_remaining != null && e.days_remaining <= 0)).length;

  const currentDecl = declarations[0];
  const fptDue = currentDecl?.amount_usd ? `${Number(currentDecl.amount_usd).toLocaleString()} USD eq.` : '--';
  const variance = currentDecl?.variance_pct != null ? `${currentDecl.variance_pct}%` : null;

  const kpis: KPI[] = [
    { label: 'Conformite FPT', value: currentDecl?.compliance_pct ? `${currentDecl.compliance_pct} %` : '-- %', note: 'Processus non bloque' },
    { label: 'FPT du', value: fptDue, note: 'Paiement ouvert' },
    { label: 'Licences valides', value: `${validLicenses}`, note: `Sur ${estCount}` },
    { label: 'A renouveler <= 90 j', value: `${expiringSoon}`, note: expiringSoon > 0 ? 'Action requise' : 'Aucune', noteColor: expiringSoon > 0 ? 'text-yellow-600' : undefined },
    { label: 'Licence expiree', value: `${expired}`, note: expired > 0 ? 'Urgent' : 'Aucune', noteColor: expired > 0 ? 'text-red-600' : undefined },
    { label: 'Renouvellements prevus', value: calendar.reduce((s: number, r: any) => s + (r.amount_usd || 0), 0).toLocaleString() + ' USD', note: '12 prochains mois' },
  ];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-5">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-600 text-white rounded-2xl p-6 flex flex-col md:flex-row justify-between gap-4 shadow">
        <div>
          <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full mb-2">Compte actif</span>
          <h2 className="text-2xl font-bold">FPT et licences de vos etablissements</h2>
          <p className="text-blue-200 text-sm mt-1">Le paiement reste autorise meme lorsqu'un ecart superieur a 5 % declenche une analyse administrative interne.</p>
        </div>
        <div className="text-right shrink-0">
          <strong className="text-lg">{assujetti?.name || '--'}</strong>
          <div className="text-blue-200 text-sm">{estCount} etablissement(s) autorise(s)</div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border rounded-xl p-4 shadow-sm">
            <div className="text-xs text-gray-500 mb-1">{k.label}</div>
            <div className="text-xl font-bold">{k.value}</div>
            <div className={`text-xs mt-1 ${k.noteColor || 'text-green-600'}`}>{k.note}</div>
          </div>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Dossier FPT */}
        <div className="lg:col-span-3 bg-white border rounded-xl p-5 shadow-sm">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-semibold">Dossier FPT -- {currentDecl?.period || 'Periode courante'}</h3>
              {variance && <div className="text-sm text-gray-500">Declaration modifiee : ecart USD {variance}</div>}
            </div>
            <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">PAIEMENT AUTORISE</span>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-lg p-3 mb-4">
            <strong>Information :</strong> une difference superieure a 5 % a ete detectee. Votre declaration et votre paiement continuent normalement. Une justification peut etre jointe au dossier.
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/fpt/v4/dossier" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700">Poursuivre le dossier</Link>
            <button className="border px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50">Joindre une justification</button>
          </div>
        </div>

        {/* Deadlines */}
        <div className="lg:col-span-2 bg-white border rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold mb-3">Prochaines echeances</h3>
          {calendar.length === 0 && <p className="text-gray-400 text-sm">Aucune echeance</p>}
          <div className="relative pl-6 space-y-4">
            {calendar.length > 0 && <div className="absolute left-2 top-1 bottom-1 w-0.5 bg-gray-200" />}
            {calendar.slice(0, 5).map((r: any, i: number) => (
              <div key={i} className="relative">
                <div className="absolute -left-4 top-1 w-3 h-3 rounded-full bg-blue-600 border-2 border-blue-100" />
                <div className="text-xs text-gray-400">{r.date || r.renewal_date || '--'}</div>
                <div className="font-medium text-sm">{r.establishment_name || r.name || '--'}</div>
                <div className="text-xs text-gray-500">{r.category || ''} {r.amount_usd ? `${r.amount_usd} USD` : ''}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
